import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, ArrowRight, User, FileUp, Check,
  Bot, HeartPulse, Coins, Route as RouteIcon, FileText, Mic, Wallet, Briefcase, Gauge,
} from "lucide-react";
import { api } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Widget } from "@/components/dashboard/Widget";

/** Sub-component: single step card (numbered, with CTA). */
const StepCard = ({ n, done, title, description, ctaLabel, ctaTo, icon: Icon, testId }) => (
  <div
    className={`relative rounded-xl border p-4 transition-colors duration-200 ${
      done ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/[0.06]" : "border-border bg-secondary/30 hover:border-primary/40"
    }`}
    data-testid={testId}
  >
    <div className="flex items-start gap-3">
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          done ? "bg-[hsl(var(--success))] text-white" : "bg-primary/10 text-primary"
        }`}
      >
        {done ? <Check size={18} strokeWidth={2.5} /> : <Icon size={16} strokeWidth={2} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Step {n}
          </span>
          {done && (
            <span className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--success))] font-semibold">
              · Completed
            </span>
          )}
        </div>
        <h4 className="font-display font-bold text-sm sm:text-base text-foreground leading-tight mb-1">{title}</h4>
        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-3 max-w-lg">
          {description}
        </p>
        {!done && (
          <Link to={ctaTo}>
            <Button size="sm" className="gap-1.5" data-testid={`${testId}-cta`}>
              {ctaLabel} <ArrowRight size={13} strokeWidth={2} />
            </Button>
          </Link>
        )}
      </div>
    </div>
  </div>
);

const UNLOCKS = [
  { icon: Bot, label: "Personalized AI Career Advisor" },
  { icon: HeartPulse, label: "Career Health Score" },
  { icon: Coins, label: "Salary Intelligence" },
  { icon: Gauge, label: "Skill Gap Analysis" },
  { icon: FileText, label: "Resume Optimization" },
  { icon: Mic, label: "Interview Preparation" },
  { icon: Wallet, label: "Financial & Retirement Planning" },
  { icon: RouteIcon, label: "Career Roadmap" },
  { icon: Briefcase, label: "Job Recommendations" },
];

/**
 * WelcomeOnboardingCard
 *
 * Rendered on the Dashboard until both onboarding steps (Profile + Resume) are
 * complete. Exposes onDone() so the parent can swap in the AI Command Center.
 */
export const WelcomeOnboardingCard = ({ onDone }) => {
  const [state, setState] = useState({ loading: true, data: null });

  const load = async () => {
    // Guard against unauth tabs firing this on visibilitychange
    if (!localStorage.getItem("token")) return;
    try {
      const { data } = await api.get("/dashboard/onboarding");
      setState({ loading: false, data });
      if (data.onboarding_done) {
        track(EVENTS.ONBOARDING_COMPLETED);
        onDone?.();
      }
    } catch {
      setState({ loading: false, data: null });
    }
  };

  useEffect(() => { load(); }, []);

  // Re-check every time the tab becomes visible again — if the user completes
  // profile/resume in another tab we transition seamlessly.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  if (state.loading) {
    return (
      <Widget size="full" padding="p-6" testId="welcome-onboarding-loading">
        <div className="skeleton h-6 w-64 mb-3" />
        <div className="skeleton h-4 w-full mb-2" />
        <div className="skeleton h-4 w-3/4 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="skeleton h-28 w-full" />
          <div className="skeleton h-28 w-full" />
        </div>
      </Widget>
    );
  }

  const data = state.data;
  if (!data) return null;
  if (data.onboarding_done) return null; // Command Center takes over.

  const { profile_complete, resume_uploaded, steps_completed, total_steps } = data;
  const progressPct = Math.round((steps_completed / total_steps) * 100);

  return (
    <motion.div
      key="welcome-onboarding-card"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.4 }}
    >
      <Widget
        size="full"
        padding="p-6 sm:p-8"
        className="relative overflow-hidden bg-gradient-to-br from-primary/[0.08] via-card to-card border-primary/20"
        testId="welcome-onboarding-card"
      >
          {/* Ambient glow */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 bg-primary/10 blur-3xl rounded-full" />

          <div className="relative">
            {/* Header */}
            <div className="font-mono text-[10px] uppercase tracking-widest text-primary flex items-center gap-1.5 mb-2">
              <Sparkles size={11} strokeWidth={2} /> Welcome to CareerOS
            </div>
            <h2
              className="font-display font-black text-2xl sm:text-3xl leading-tight tracking-tight text-foreground mb-2"
              data-testid="welcome-onboarding-title"
            >
              Let&apos;s build your personalized career strategy.
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-3xl leading-relaxed mb-6">
              To unlock AI-powered career insights, salary analysis, interview coaching, financial
              planning, and personalized recommendations, we first need to learn about you.
            </p>

            {/* Progress bar */}
            <div
              className="mb-6 max-w-md"
              data-testid="welcome-onboarding-progress"
              aria-label={`Onboarding progress: ${steps_completed} of ${total_steps} steps completed`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Progress
                </span>
                <span className="text-xs font-mono font-semibold text-foreground">
                  <span data-testid="welcome-onboarding-progress-count">
                    {steps_completed} / {total_steps}
                  </span>{" "}
                  <span className="text-muted-foreground">steps completed</span>
                </span>
              </div>
              <div
                className="h-2 rounded-full bg-secondary overflow-hidden"
                role="progressbar"
                aria-valuenow={steps_completed}
                aria-valuemin={0}
                aria-valuemax={total_steps}
              >
                <motion.div
                  className="h-full rounded-full bg-primary"
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
              </div>
            </div>

            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <StepCard
                n={1}
                done={profile_complete}
                icon={User}
                title="Complete Your Profile"
                description="Tell us about your experience, education, skills, location, salary, and career goals so we can personalize every recommendation."
                ctaLabel="Complete Profile"
                ctaTo="/app/profile"
                testId="welcome-onboarding-step-1"
              />
              <StepCard
                n={2}
                done={resume_uploaded}
                icon={FileUp}
                title="Upload Your Resume"
                description="Upload your current resume and let CareerOS analyze your experience, identify strengths, uncover skill gaps, and generate personalized career opportunities."
                ctaLabel="Upload Resume"
                ctaTo="/app/resume"
                testId="welcome-onboarding-step-2"
              />
            </div>

            {/* Unlocks */}
            <div className="rounded-xl border border-border/70 bg-secondary/20 p-4 sm:p-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2.5">
                Your dashboard will come alive · you&apos;ll unlock
              </div>
              <ul
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
                data-testid="welcome-onboarding-unlocks"
              >
                {UNLOCKS.map(({ icon: Icon, label }, i) => (
                  <motion.li
                    key={label}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90"
                  >
                    <span className="w-6 h-6 rounded-md bg-primary/10 text-primary flex-shrink-0 flex items-center justify-center">
                      <Icon size={12} strokeWidth={2} />
                    </span>
                    {label}
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
      </Widget>
    </motion.div>
  );
};
